import { forwardRef, useImperativeHandle, useState, useEffect } from 'react'
import { FormControl, Text, FormErrorMessage, Input, Grid, GridItem } from '~/design_system'
import { useMsg, Msg } from '~/common/localizationUtils'
import { getAddressConfig } from '~/utils/address'
import VndlySelect from '~/components/VndlySelect'
import {
  DEFAULT_COUNTRY_CODE,
  postErrorTransformation,
  postRequireFieldTransformation,
  getSubdivisionOptions,
  getAddressFields
} from './addressUtils'

async function setRemovedFieldAsEmpty(prevCountryCode, nextCountryCode) {
  const toRemove = {}
  const [oldConfig, newConfig] = await Promise.all([
    getAddressFields(prevCountryCode),
    getAddressFields(nextCountryCode)
  ])
  const removedFields = oldConfig[prevCountryCode]
    .filter(of => !newConfig[nextCountryCode].some(nf => nf.field === of.field))
    .map(el => el.field)
  removedFields.forEach(field => {
    toRemove[field] = ''
  })

  return toRemove
}

function getRequiredFields(config, readOnlyFields) {
  return config?.reduce(
    (acc, c) => {
      acc[c.field] = c.required && !readOnlyFields.includes(c.field)
      return acc
    },
    { country_code: true }
  )
}

function transformAddressRequirements(requirements, countryCode) {
  if (!requirements || !(countryCode in requirements)) {
    return {}
  }
  return requirements[countryCode] ? requirements[countryCode].map(requirement => ({ ...requirement, colSpan: 1 })) : []
}

const AddressSubForm = ({
  values,
  errors,
  requiredFields = {}, // TODO: deprecate this or turn this into an override
  countriesList,
  subdivisionsList,
  readOnly = false,
  readOnlyFields = [],
  onChange = () => {},
  onBlur = () => {},
  onCountryBlur = () => {},
  id = 'address_form',
  useExtendedAddressRequirements = false,
  ignore_country_address_requirements = false
}) => {
  const countryCode = values?.country_code?.toUpperCase() ?? DEFAULT_COUNTRY_CODE
  const selectedCountry = countriesList.length > 0 ? countriesList.find(s => s.value === countryCode) : ''
  const config = getAddressConfig(countryCode)
  const [addressRequirements, setAddressRequirements] = useState([])
  const configDefinedRequiredFields = getRequiredFields(
    useExtendedAddressRequirements ? addressRequirements : config,
    readOnlyFields
  )
  const required =
    Object.keys(requiredFields).length > 0 && !useExtendedAddressRequirements
      ? requiredFields
      : Object.fromEntries(
          Object.entries(configDefinedRequiredFields).map(([key, value]) => [
            key,
            (value && !ignore_country_address_requirements) || (key in requiredFields ? requiredFields[key] : false)
          ])
        )
  const filteredConfig = (useExtendedAddressRequirements ? addressRequirements : config).filter(
    el => (el.field === 'state' && subdivisionsList.length > 0) || el.field !== 'state'
  )
  const selectedSubdiv = subdivisionsList.length > 0 ? subdivisionsList.find(s => s.value === values.state) : ''
  const msg = useMsg()

  const onFormChange = evt => {
    onChange({ [evt.target.name]: evt.target.value })
  }

  const onFormChangeCountry = async evt => {
    const { value } = evt
    const removedFields = await setRemovedFieldAsEmpty(countryCode, value)
    const toChange = { ...removedFields, country_code: value, state: '' }
    onChange(toChange)
  }

  const onFormChangeSubdivision = evt => {
    onChange({ state: evt.value })
  }

  const handleCountryBlur = () => {
    onCountryBlur('country_code')
  }

  const onInputBlur = () => {
    onBlur()
  }

  const setRequirements = async countryCode => {
    setAddressRequirements(transformAddressRequirements(await getAddressFields(countryCode), countryCode))
  }

  useEffect(() => {
    setRequirements(countryCode)
  }, [values?.country_code])

  let countryError
  if (errors.country_code) {
    countryError = msg('common.ui.country_is_required')
  }
  const isCountryReadOnly = readOnly || 'country_code' in readOnlyFields
  const isCountryRequired = required?.country_code || required?.country_id

  return (
    <Grid templateColumns="repeat(2, 1fr)" columnGap={4}>
      <GridItem colSpan={2}>
        <FormControl mb={15} className="form-group" isInvalid={'country_code' in errors ? true : undefined}>
          <Text htmlFor="name" fontWeight="bold">
            <Msg id="address_field.country" description="Country" />
            {isCountryRequired && '*'}
          </Text>
          <VndlySelect
            id={`${id}_country`}
            testId="select-country"
            options={countriesList}
            value={selectedCountry}
            onChange={onFormChangeCountry}
            onBlur={handleCountryBlur}
            readOnly={isCountryReadOnly}
          />
          <FormErrorMessage>{countryError}</FormErrorMessage>
        </FormControl>
      </GridItem>
      {filteredConfig?.map(el => {
        const { field, label, colSpan } = el
        let fieldError
        // Non-formik validation of "is field required" returns boolean while the formik schema validation (candidate form) returns "required" in error value when validation fails
        // TODO: change non-formik validation check to return `required` instead of boolean
        const hasRequiredFieldError = errors[field] === true || errors[field] === 'required'
        if (errors[field]) {
          if (hasRequiredFieldError) {
            fieldError = `${msg('common.ui.term_is_required', { term: msg(label) })}`
          } else {
            fieldError = errors[field]
          }
        }
        if (field === 'state' || field === 'subdivision') {
          const placeholder = `${msg('common.ui.fields.placeholder.select', { field_label: msg(label) })}`
          return (
            <GridItem key={`${field}-${values.country_code}-${subdivisionsList.length}`} colSpan={colSpan}>
              <FormControl
                mb={15}
                className="form-group"
                isInvalid={Object.prototype.hasOwnProperty.call(errors, 'state') ? true : undefined}>
                <div id={`${id}_${field}`}>
                  <Text htmlFor="name" fontWeight="bold">
                    {msg(label)}
                    {required && required[field] && '*'}
                  </Text>
                  <VndlySelect
                    id={`${id}_subdivisions`}
                    testId="select-subdivision"
                    options={subdivisionsList}
                    value={selectedSubdiv}
                    onChange={onFormChangeSubdivision}
                    placeholder={placeholder}
                    onBlur={onInputBlur}
                    readOnly={readOnly || readOnlyFields.includes(field)}
                  />
                </div>
                <FormErrorMessage>{fieldError}</FormErrorMessage>
              </FormControl>
            </GridItem>
          )
        }
        return (
          <GridItem key={field} colSpan={colSpan}>
            <FormControl
              mb={15}
              className="form-group"
              isInvalid={Object.prototype.hasOwnProperty.call(errors, field) ? true : undefined}>
              <Text htmlFor="name" fontWeight="bold">
                {msg(label)}
                {required && required[field] && '*'}
              </Text>
              <Input
                id={`${id}_${field}`}
                className="form-control"
                placeholder={el.placeholder || msg(label)}
                name={field}
                isReadOnly={readOnly || readOnlyFields.includes(field)}
                value={values[field]}
                onChange={onFormChange}
                onBlur={onInputBlur}
                data-testid={field}
              />
              <FormErrorMessage>{fieldError}</FormErrorMessage>
            </FormControl>
          </GridItem>
        )
      })}
    </Grid>
  )
}

const UncontrolledAddressSubForm = forwardRef(
  (
    {
      id,
      initialErrors = {},
      countriesList = [],
      initialValues = {},
      readOnlyFields = [],
      requiredFields = {}, // TODO: deprecate this or turn this into an override
      readOnly = false,
      onChange = () => {},
      useNewAddressRequirements = false
    },
    ref
  ) => {
    const [config, setConfig] = useState(getAddressConfig(DEFAULT_COUNTRY_CODE))
    const configDefinedRequiredFields = getRequiredFields(config, readOnlyFields)
    const [required, setRequired] = useState(
      Object.keys(requiredFields).length > 0 ? requiredFields : configDefinedRequiredFields
    )
    const [errors, setErrors] = useState({})
    const [values, setValues] = useState(initialValues || {})

    const [subdivisionsList, setSubdivisionsList] = useState([])
    const [selectedCountry, setSelectedCountry] = useState()
    const [selectedSubdiv, setSelectedSubdiv] = useState()

    const imperativeHandles = {
      getAddressData: () => ({
        ...values
      }),
      getRequiredAddressFields: () => ({
        ...required
      }),
      getAddressErrors: () => ({
        ...errors
      }),
      refreshAddressErrors: async () => {
        const updatedErrors = await postErrorTransformation(values, required, useNewAddressRequirements)
        setErrors({ ...updatedErrors })
        return { ...updatedErrors }
      }
    }

    useImperativeHandle(ref, () => imperativeHandles, [imperativeHandles])

    const updateSubdivisionSelect = (subdivision, sList = subdivisionsList) => {
      const selectedOption = sList.length > 0 && subdivision ? sList.find(s => s.value === subdivision) : ''
      setSelectedSubdiv(selectedOption)
      return selectedOption?.value || ''
    }

    const onFormChange = evt => {
      const updatedValues = { ...values, [evt.target.name]: evt.target.value }
      setValues(updatedValues)
      onChange(updatedValues)
    }

    const onInputBlur = async () => {
      const updatedErrors = await postErrorTransformation(values, required, useNewAddressRequirements)
      setErrors({ ...updatedErrors })
    }

    const onFormChangeCountry = async evt => {
      const { value } = evt
      let updatedRequiredFields = { ...requiredFields }
      const removedFields = await setRemovedFieldAsEmpty(values?.country_code, value, useNewAddressRequirements)
      const updatedValues = { ...removedFields, ...values, country_code: value, state: '' }
      const subdivisionOptions = await getSubdivisionOptions(value)
      const selectedSubdivision = updateSubdivisionSelect(values.state, subdivisionOptions)
      if (selectedSubdivision) {
        updatedValues.state = selectedSubdivision
      }
      const config = useNewAddressRequirements ? await getAddressFields(value) : getAddressConfig(value)
      const filteredConfig = (useNewAddressRequirements ? config[value] : config).filter(
        el => (el.field === 'state' && subdivisionOptions.length > 0) || el.field !== 'state'
      )
      if (Object.keys(requiredFields).length > 0) {
        updatedRequiredFields = postRequireFieldTransformation(updatedRequiredFields, subdivisionOptions)
      } else {
        updatedRequiredFields = getRequiredFields(useNewAddressRequirements ? config[value] : config, readOnlyFields)
      }
      const updatedErrors = await postErrorTransformation(
        updatedValues,
        updatedRequiredFields,
        useNewAddressRequirements
      )
      // 4. Update address related states
      setSubdivisionsList(subdivisionOptions)
      setConfig(filteredConfig)
      setRequired(updatedRequiredFields)
      setSelectedCountry(evt)
      setValues(updatedValues)
      setErrors({ ...updatedErrors })
      onChange(updatedValues)
    }

    const onFormChangeSubdivision = async evt => {
      const updatedValues = { ...values, state: evt.value }
      const updatedErrors = await postErrorTransformation(updatedValues, required, useNewAddressRequirements)
      setValues(updatedValues)
      setErrors({ ...updatedErrors })
      updateSubdivisionSelect(evt.value)
      onChange(updatedValues)
    }

    useEffect(() => {
      async function updateAsync() {
        const countryCode = initialValues.country_code ?? DEFAULT_COUNTRY_CODE
        const country = countriesList.find(s => s.value === countryCode)
        const subdivisionOptions = await getSubdivisionOptions(countryCode)
        const config = useNewAddressRequirements ? await getAddressFields(countryCode) : getAddressConfig(countryCode)
        let updatedRequiredFields = null
        if (Object.keys(requiredFields).length > 0) {
          updatedRequiredFields = postRequireFieldTransformation(updatedRequiredFields, subdivisionOptions)
        } else {
          updatedRequiredFields = getRequiredFields(
            useNewAddressRequirements ? config[countryCode] : config,
            readOnlyFields
          )
        }

        // update address form config to set country value
        if (country) {
          const filteredConfig = (useNewAddressRequirements ? config[countryCode] : config).filter(
            el => (el.field === 'state' && subdivisionOptions.length > 0) || el.field !== 'state'
          )
          setConfig(filteredConfig)
        }

        // 2. Get updated subdivisions
        updateSubdivisionSelect(initialValues.state, subdivisionOptions)
        setRequired(updatedRequiredFields)
        setValues(initialValues)
        setErrors(initialErrors)
        setSelectedCountry(country)
        setSubdivisionsList(subdivisionOptions)
      }
      updateAsync()
    }, [initialValues])

    useEffect(() => {
      window[`react_${id}`] = imperativeHandles
    }, [imperativeHandles])
    const msg = useMsg()

    let countryError
    // TODO (Tom C): Remove residence_country check after work_site residence_country fk is removed
    if ('residence_country' in errors || 'country_code' in errors) {
      countryError = msg('common.ui.country_is_required')
    }
    const isCountryReadOnly =
      readOnly || readOnlyFields.includes('residence_country') || readOnlyFields.includes('country_code')
    return (
      <Grid templateColumns="repeat(2, 1fr)" columnGap={4}>
        <GridItem colSpan={2}>
          <FormControl
            mb={15}
            className="form-group select-wrapper"
            isInvalid={'residence_country' in errors || 'country_code' in errors ? true : undefined}>
            <Text htmlFor="name" fontWeight="semibold">
              <Msg id="address_field.country" description="Country" />
              {required && (required?.residence_country || required?.country_code) && '*'}
            </Text>
            <VndlySelect
              id={`${id}_country`}
              testId="select-country"
              options={countriesList}
              value={selectedCountry}
              onChange={onFormChangeCountry}
              style={{ height: '35px', minHeight: '35px' }}
              autoComplete="off"
              readOnly={isCountryReadOnly}
              onBlur={onInputBlur}
            />
            <FormErrorMessage>{countryError}</FormErrorMessage>
          </FormControl>
        </GridItem>
        {config.map(el => {
          const { field, label, colSpan } = el
          let fieldError
          if (errors[field]) {
            if (field === 'zip' && errors[field] !== true) {
              fieldError = errors[field]
            } else {
              fieldError = `${msg('common.ui.term_is_required', { term: msg(label) })}`
            }
          }
          if (field === 'state' || field === 'subdivision') {
            return (
              <GridItem key={`${field}-${values.country_code}-${subdivisionsList.length}`} colSpan={colSpan}>
                <FormControl
                  className="form-group select-wrapper"
                  isInvalid={Object.prototype.hasOwnProperty.call(errors, 'state') ? true : undefined}>
                  <div id={`${id}_${field}`}>
                    <Text htmlFor="name" fontWeight="semibold">
                      {msg(label)}
                      {required && required[field] && '*'}
                    </Text>
                    <VndlySelect
                      testId="select-subdivision"
                      options={subdivisionsList}
                      value={selectedSubdiv}
                      onChange={onFormChangeSubdivision}
                      placeholder={`${msg('common.ui.fields.placeholder.select', { field_label: msg(label) })}`}
                      style={{ height: '35px', minHeight: '35px' }}
                      readOnly={readOnly || readOnlyFields.includes(field)}
                      onBlur={onInputBlur}
                    />
                  </div>
                  <FormErrorMessage>{fieldError}</FormErrorMessage>
                </FormControl>
              </GridItem>
            )
          }
          return (
            <GridItem key={field} colSpan={colSpan}>
              <FormControl
                className="form-group"
                isInvalid={Object.prototype.hasOwnProperty.call(errors, field) ? true : undefined}>
                <Text htmlFor="name" fontWeight="semibold">
                  {msg(label)}
                  {required && required[field] && '*'}
                </Text>
                <Input
                  id={`${id}_${field}`}
                  className="no-style"
                  placeholder={el.placeholder || msg(label)}
                  name={field}
                  value={values[field]}
                  onChange={onFormChange}
                  data-testid={field}
                  borderColor="#cfdae6"
                  borderRadius={3}
                  height={35}
                  isReadOnly={readOnly || readOnlyFields.includes(field)}
                  fontSize="15px"
                  onBlur={onInputBlur}
                />
                <FormErrorMessage>{fieldError}</FormErrorMessage>
              </FormControl>
            </GridItem>
          )
        })}
      </Grid>
    )
  }
)

export default AddressSubForm

export { UncontrolledAddressSubForm }
